import { RotateCcw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import type { AuditFilters as AuditFilterValues } from "../types";

type Props = {
  filters: AuditFilterValues;
  onChange: (next: AuditFilterValues) => void;
  onReset: () => void;
};

const PRESETS = [
  { value: "", label: "All time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7days", label: "Last 7 days" },
  { value: "last30days", label: "Last 30 days" },
  { value: "last90days", label: "Last 90 days" },
];

const SEVERITIES = [
  { value: "", label: "All severity" },
  { value: "INFO", label: "Info" },
  { value: "WARNING", label: "Warning" },
  { value: "ERROR", label: "Error" },
  { value: "CRITICAL", label: "Critical" },
];

export function AuditFilters({ filters, onChange, onReset }: Props) {
  function set<K extends keyof AuditFilterValues>(
    key: K,
    value: AuditFilterValues[K],
  ) {
    onChange({ ...filters, [key]: value || undefined, page: 1 });
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative w-[280px]">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          strokeWidth={1.5}
        />
        <Input
          placeholder="Search events..."
          value={filters.search ?? ""}
          onChange={(e) => set("search", e.target.value)}
          className="pl-9"
        />
      </div>
      <Select
        value={filters.preset ?? ""}
        onChange={(e) => set("preset", e.target.value || undefined)}
        className="w-40"
      >
        {PRESETS.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
      </Select>
      <Select
        value={filters.severity ?? ""}
        onChange={(e) =>
          set(
            "severity",
            (e.target.value || undefined) as AuditFilterValues["severity"],
          )
        }
        className="w-36"
      >
        {SEVERITIES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </Select>
      <Button variant="outline" onClick={onReset} className="ml-auto">
        <RotateCcw className="h-4 w-4" strokeWidth={1.5} />
        Reset
      </Button>
    </div>
  );
}
